// Regras puras do acordo de parcelamento: conta de cobrança, alçada e origem do
// desconto. Chamados #5686 e #6174.
//
// Vive separado de `parcelamentoAcordo.ts` porque aquele importa o cliente
// Supabase, que lê `import.meta.env` e não roda sob `node --test`. Aqui não há
// I/O nenhum — e são exatamente as regras que, se falharem, cobram na conta
// errada ou passam por cima da alçada. Precisam de teste.

import { formatarBRL } from './parcelamentoCalculo.ts';
import { isContaSicoob, NOME_CONTA_SICOOB } from './contaSicoob.ts';

export interface FaturaGateway {
  id?: string | number;
  conta_sicoob?: string | null;
  empresa_parceira_id?: string | null;
  vencimento_boleto?: string | null;
  uc_codigo?: string | null;
}

export interface AvisoGateway {
  campo: 'conta_sicoob' | 'empresa_parceira_id' | 'uc_codigo';
  valores: string[];
  mensagem: string;
}

export interface Pagador {
  cliente_cpf_cnpj?: string | null;
  cliente_nome?: string | null;
  cpf_cnpj_cliente_boleto?: string | null;
  nome_cliente_boleto?: string | null;
}

/** Quem deve a fatura é quem paga o boleto, não o titular da conta de luz.
 *
 *  Os dois divergem em 228 das 1.338 faturas em aberto (set/2026): a UC está
 *  no nome de um (ex.: o dono do imóvel) e o boleto sai no de outro (o
 *  inquilino, cliente da Vigor). O boleto do acordo já sai no pagador — ver
 *  `buscarReferenciaCobranca` —, então é por ele que a dívida se junta.
 *  Fatura sem pagador gravado cai no titular. */
export const docPagador = (f: Pagador) =>
  String(f.cpf_cnpj_cliente_boleto || f.cliente_cpf_cnpj || '').replace(/\D/g, '');
export const nomePagador = (f: Pagador) =>
  String(f.nome_cliente_boleto || f.cliente_nome || '').trim().toUpperCase();

/**
 * Duas faturas são do mesmo pagador?
 *
 * É o que decide quais faturas a tela de acordo marca sozinha — errar aqui põe
 * a dívida de outra pessoa dentro do termo.
 *
 * O documento manda quando os dois lados têm um: o mesmo CPF aparece escrito de
 * jeitos diferentes nas faturas ("JOSE DA SILVA" e "JOSE DA SILVA ME"), e
 * comparar nome deixaria parte da dívida de fora justo no caso que a marcação
 * automática existe para resolver — 14 titulares com débito estão nessa
 * situação hoje. Quando um dos lados não tem documento (2.143 faturas, e as
 * semeadas por deep-link, que não carregam o CPF), cai no nome, que é o que
 * sobra. Sem documento e sem nome dos dois lados não há como afirmar nada: dois
 * vazios não são a mesma pessoa.
 */
export function mesmoPagador(a: Pagador, b: Pagador): boolean {
  const da = docPagador(a);
  const db = docPagador(b);
  if (da && db) return da === db;
  const na = nomePagador(a);
  const nb = nomePagador(b);
  if (!na || !nb) return false;
  return na === nb;
}

/** null, undefined e '' são o MESMO caso: fatura da Vigor na conta padrão.
 *  Tratar separado marcaria divergência onde não há. */
const normalizar = (v: unknown) => (v === null || v === undefined || v === '' ? '' : String(v));

const rotuloConta = (v: string) => (isContaSicoob(v) ? NOME_CONTA_SICOOB[v] : v || 'padrão');
const rotuloEmpresa = (v: string) => v || 'Vigor';

export interface ContaCobranca {
  faturaId: string | null;
  conta_sicoob: string | null;
  empresa_parceira_id: string | null;
  uc_codigo: string | null;
}

/**
 * De qual fatura o acordo herda conta bancária, empresa e UC.
 *
 * O acordo não "continua" as faturas antigas: ele é uma dívida NOVA, com boleto
 * novo. Por isso não precisa que as faturas concordem entre si — precisa só
 * escolher uma conta, e escolher sempre a mesma.
 *
 * A escolha é a fatura de vencimento MAIS RECENTE, não a primeira da lista: ela
 * reflete o arranjo de cobrança atual do cliente (conta e empresa em que ele
 * está sendo faturado hoje). Fatura sem vencimento vai para o fim. Empate
 * mantém a ordem recebida, para o resultado não depender da ordenação da busca.
 */
export function resolverContaCobranca(faturas: FaturaGateway[]): ContaCobranca {
  const vazio: ContaCobranca = {
    faturaId: null, conta_sicoob: null, empresa_parceira_id: null, uc_codigo: null,
  };
  if (faturas.length === 0) return vazio;

  const escolhida = faturas.reduce((melhor, atual) => {
    const dataMelhor = normalizar(melhor.vencimento_boleto);
    const dataAtual = normalizar(atual.vencimento_boleto);
    if (!dataAtual) return melhor;
    if (!dataMelhor) return atual;
    // ISO compara como texto. `>` e não `>=` mantém o primeiro em caso de empate.
    return dataAtual > dataMelhor ? atual : melhor;
  });

  return {
    faturaId: escolhida.id === undefined || escolhida.id === null ? null : String(escolhida.id),
    conta_sicoob: escolhida.conta_sicoob ?? null,
    empresa_parceira_id: escolhida.empresa_parceira_id ?? null,
    uc_codigo: escolhida.uc_codigo ?? null,
  };
}

/**
 * Avisos — NÃO travas — sobre juntar faturas heterogêneas num acordo só.
 *
 * A versão anterior desta função barrava o acordo quando as faturas vinham de
 * contas Sicoob, empresas ou UCs diferentes. Isso estava errado na prática: o
 * acordo é por PAGADOR. Quem deve 4 meses em duas UCs, ou tem fatura antiga
 * numa conta e recente em outra, é uma pessoa só e negocia a dívida inteira de
 * uma vez. Travar obrigava a abrir um acordo por conta e por UC — vários termos
 * e várias entradas para o mesmo cliente.
 *
 * O que sobrou como risco real é contábil, não operacional: o dinheiro do
 * acordo cai todo numa conta. Por isso cada aviso diz em qual. Os boletos
 * antigos não são afetados — cada um é baixado na sua própria conta pela
 * efetivação.
 */
export function analisarGateway(faturas: FaturaGateway[]): AvisoGateway[] {
  const avisos: AvisoGateway[] = [];
  if (faturas.length < 2) return avisos;

  const alvo = resolverContaCobranca(faturas);

  const contas = [...new Set(faturas.map((f) => normalizar(f.conta_sicoob)))];
  if (contas.length > 1) {
    avisos.push({
      campo: 'conta_sicoob',
      valores: contas.map(rotuloConta),
      mensagem:
        `As faturas vêm de contas Sicoob diferentes (${contas.map(rotuloConta).join(', ')}). ` +
        `O acordo é uma cobrança nova e sai inteiro na conta ${rotuloConta(normalizar(alvo.conta_sicoob))}, ` +
        'a da fatura mais recente. Os boletos antigos continuam sendo baixados cada um na sua conta.',
    });
  }

  const empresas = [...new Set(faturas.map((f) => normalizar(f.empresa_parceira_id)))];
  if (empresas.length > 1) {
    avisos.push({
      campo: 'empresa_parceira_id',
      valores: empresas.map(rotuloEmpresa),
      mensagem:
        'As faturas são de empresas diferentes (Vigor e parceira, ou duas parceiras). ' +
        `O acordo inteiro será cobrado por ${rotuloEmpresa(normalizar(alvo.empresa_parceira_id))} — ` +
        'o acerto entre as empresas precisa ser feito à parte.',
    });
  }

  const ucs = [...new Set(faturas.map((f) => normalizar(f.uc_codigo)).filter(Boolean))];
  if (ucs.length > 1) {
    avisos.push({
      campo: 'uc_codigo',
      valores: ucs,
      mensagem:
        `O acordo junta a dívida de ${ucs.length} unidades consumidoras do mesmo pagador. ` +
        `A entrada e as parcelas saem na UC ${alvo.uc_codigo || ucs[0]}, e as faturas de todas as ` +
        'UCs escolhidas são quitadas pelo acordo.',
    });
  }

  return avisos;
}

export interface LimitesAlcada {
  /** Taxa mensal (%) abaixo da qual o acordo exige aprovação. 0 desliga. */
  taxaJurosMinima: number;
  /** Desconto (%) sobre o saldo acima do qual o acordo exige aprovação. 100 desliga. */
  descontoSaldoMaximo: number;
}

export interface MotivoAprovacao {
  motivo: string;
}

/** Decide se o acordo nasce travado esperando aprovação nominal. */
export function avaliarAlcada(
  taxaJurosMensalPercent: number,
  descontoSaldoPercent: number | null,
  limites: LimitesAlcada,
): MotivoAprovacao[] {
  const motivos: MotivoAprovacao[] = [];

  // Mínimo 0 desliga a trava — é o default, para a feature não travar a
  // operação no dia em que sobe.
  if (limites.taxaJurosMinima > 0 && taxaJurosMensalPercent < limites.taxaJurosMinima) {
    motivos.push({
      motivo: `Taxa de juros de ${taxaJurosMensalPercent}% a.m. abaixo do mínimo de ${limites.taxaJurosMinima}% a.m.`,
    });
  }

  if (
    descontoSaldoPercent !== null &&
    descontoSaldoPercent !== undefined &&
    limites.descontoSaldoMaximo < 100 &&
    descontoSaldoPercent > limites.descontoSaldoMaximo
  ) {
    motivos.push({
      motivo: `Desconto de ${descontoSaldoPercent}% sobre o saldo acima do máximo de ${limites.descontoSaldoMaximo}%.`,
    });
  }

  return motivos;
}

// ------------------------------------------------- origem do desconto (#6174)

/**
 * De onde veio o desconto na dívida. O mesmo campo servia para duas coisas bem
 * diferentes, e o histórico não separava uma da outra (chamado #6174):
 *
 * - `gestao`: desconto de verdade, autorizado pelos gestores. A Vigor abre mão
 *   do valor; se a entrada não for paga, o desconto deixa de valer (cláusula
 *   5.2-A do termo).
 * - `valor_ja_pago`: dinheiro que o cliente JÁ pagou — entrada de um acordo
 *   que caiu, pagamento avulso — e que agora abate a dívida. Não é desconto: o
 *   termo trata como pagamento e tira do valor confessado, para um acordo
 *   quebrado não dar margem a cobrar de novo o que já entrou.
 */
export type OrigemDesconto = 'gestao' | 'valor_ja_pago';

export const ORIGENS_DESCONTO: Record<OrigemDesconto, {
  rotulo: string;
  /** Rótulo da linha na memória de cálculo e na trilha. */
  linha: string;
  /** Rótulo do campo de descrição. */
  pergunta: string;
  exemplo: string;
  /** Para onde vai a descrição — o analista precisa saber se o cliente vai ler. */
  destino: string;
}> = {
  gestao: {
    rotulo: 'Desconto autorizado pela gestão',
    linha: 'Desconto da gestão',
    pergunta: 'Quem autorizou e por quê',
    exemplo: 'Ex.: autorizado pelo Vinicius em 07/10, cliente antigo',
    destino: 'Fica só no sistema. No termo sai como desconto.',
  },
  valor_ja_pago: {
    rotulo: 'Valor que o cliente já pagou',
    linha: 'Já pago antes',
    pergunta: 'O que foi pago e quando',
    exemplo: 'Ex.: entrada de R$ 5.000 paga em 11/05/2026 e R$ 4.000 em 30/06/2026',
    destino: 'Vai escrito no termo: o valor sai da dívida confessada, como pagamento, e não como desconto.',
  },
};

export const ehOrigemDesconto = (v: unknown): v is OrigemDesconto =>
  v === 'gestao' || v === 'valor_ja_pago';

/** Menos que isso não conta história nenhuma ("ok", "desc", "."). */
export const DESCRICAO_DESCONTO_MINIMA = 10;

/** O que impede de seguir com o desconto, em linguagem de operador. Vazio = pode seguir. */
export function validarOrigemDesconto(p: {
  descontoCentavos: number;
  origem: OrigemDesconto | null;
  descricao: string;
  /** Faturas + multa + mora + outros débitos. O saldo de energia fica de fora:
   *  pagamento antigo abate dívida vencida, e é dela que o termo tira o valor
   *  confessado. */
  dividaVencidaCentavos: number;
}): string[] {
  if (p.descontoCentavos <= 0) return [];
  const erros: string[] = [];
  if (!p.origem) {
    erros.push('Diga de onde vem o desconto: autorizado pela gestão ou valor que o cliente já pagou.');
  }
  if (p.descricao.trim().length < DESCRICAO_DESCONTO_MINIMA) {
    erros.push(
      p.origem === 'valor_ja_pago'
        ? 'Descreva o que o cliente já pagou e quando — é isso que vai no termo.'
        : 'Descreva o desconto: quem autorizou e por quê.',
    );
  }
  if (p.origem === 'valor_ja_pago' && p.descontoCentavos > p.dividaVencidaCentavos) {
    erros.push(
      `O valor já pago (${formatarBRL(p.descontoCentavos)}) não pode passar da dívida das faturas ` +
      `(${formatarBRL(p.dividaVencidaCentavos)}).`,
    );
  }
  return erros;
}

/** "Multa" passa; "x", "." ou "ok" não dizem ao cliente o que ele está confessando. */
export const DESCRICAO_OUTRO_DEBITO_MINIMA = 5;

/** Débitos fora do sistema (pedido de 08/10/2026), como o analista digitou. */
export interface OutroDebitoDigitado {
  descricao: string;
  valor: number | '';
}

/** O que impede de seguir com os outros débitos. Vazio = pode seguir. Cada
 *  débito vira uma linha do Anexo A do termo: sem descrição, o cliente assina
 *  um valor que não sabe de onde veio. */
export function validarOutrosDebitos(itens: OutroDebitoDigitado[]): string[] {
  const erros: string[] = [];
  if (itens.length === 0) erros.push('Adicione ao menos um débito ou desmarque "Somar outros débitos".');
  itens.forEach((d, i) => {
    const qual = itens.length > 1 ? ` ${i + 1}` : '';
    if (d.descricao.trim().length < DESCRICAO_OUTRO_DEBITO_MINIMA) {
      erros.push(`Escreva o que é o débito${qual}: é isso que sai no termo.`);
    }
    if (!(Number(d.valor) > 0)) erros.push(`Informe o valor do débito${qual}.`);
  });
  return erros;
}

/** "desconto de R$ 100,00 autorizado pela gestão" — para a trilha e o resumo. */
export function fraseDesconto(origem: OrigemDesconto | null | undefined, valor: string): string {
  if (origem === 'valor_ja_pago') return `abatimento de ${valor} que o cliente já tinha pago`;
  if (origem === 'gestao') return `desconto de ${valor} autorizado pela gestão`;
  return `desconto de ${valor}`;
}

/** Estados em que o acordo AINDA NÃO vale: faturas originais intactas. */
export const ESTADOS_PENDENTES = ['aguardando_aprovacao', 'aguardando_assinatura', 'aguardando_entrada'] as const;
/** Estados em que o acordo vale: faturas-mãe já marcadas como Parceladas. */
export const ESTADOS_VIGENTES = ['ativo', 'em_atraso', 'concluido'] as const;

export type StatusAcordo =
  | 'aguardando_aprovacao' | 'aguardando_assinatura' | 'aguardando_entrada'
  | 'ativo' | 'em_atraso' | 'concluido' | 'cancelado' | 'expirado';

export const ROTULO_STATUS: Record<StatusAcordo, string> = {
  aguardando_aprovacao: 'Aguardando aprovação',
  aguardando_assinatura: 'Aguardando assinatura',
  aguardando_entrada: 'Aguardando entrada',
  ativo: 'Ativo',
  em_atraso: 'Em atraso',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
  expirado: 'Expirado',
};

/** Um acordo só destrói a dívida original quando está vigente. Usada pela tela
 *  e pela edge function para decidir se pode mexer nas faturas-mãe. */
export function acordoVigente(status: string): boolean {
  return (ESTADOS_VIGENTES as readonly string[]).includes(status);
}

export function acordoPendente(status: string): boolean {
  return (ESTADOS_PENDENTES as readonly string[]).includes(status);
}
